import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModal } from "react-modal-hook";
import { RESOURCE_NAME } from "common/authorization";
import { buildLink } from "common/routing";
import { GetDocumentUploadsQuery } from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { createTracker, track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { Actions } from "../Common/ActionCell";
import { Button } from "../Common/Button";
import Modal from "../Common/Modal";
import {
  ActionsProps,
  shouldShowViewOnMapActionForCustomMapGeometry,
  shouldShowViewOnMapActionForProperty,
} from "../Inputs/DropdownMenu";
import { useIssueModal } from "../Issues";
import { DeleteDocumentUploadConfirmation } from "./DeleteConfirmation";
import { useShareFileModal } from "./ElevationCertificates/ShareFileModal";

export interface ActionButtonProps {
  isDetailView?: boolean;
  disabled?: boolean;
  httpClient?: typeof fetch;
  onUpdate?: () => void;
  documentUpload: Omit<
    GetDocumentUploadsQuery["documentUploads"]["data"][number],
    "url" | "name" | "property" | "elevationCertificate"
  > & {
    property?: Maybe<{
      id: string;
      streetAddress?: Maybe<string>;
      longitude: number;
      latitude: number;
    }>;
    customMapGeometry?: Maybe<{
      id: string;
      customMapId?: string;
    }>;
    elevationCertificate?: Maybe<{
      id: string;
      issuedAt?: any | null;
      certfierInfo?: {
        name: Maybe<string>;
        email: Maybe<string>;
      };
    }>;
    isDeletable?: boolean;
  };
}

const ActionButton = ({
  documentUpload,
  isDetailView = false,
  disabled = false,
  httpClient = fetch,
  onUpdate,
}: ActionButtonProps) => {
  const {
    property,
    customMapGeometry,
    elevationCertificate,
    issuedAt,
    accountDocumentType,
    id: documentUploadId,
    originalFilename,
    formData,
    isDeletable,
    hiddenFromPublic,
  } = documentUpload;

  const coordinates = [property?.longitude!, property?.latitude!] as const;

  const [loadingDocxExport, setLoadingDocxExport] = useState(false);
  const { addErrorToast } = useStatusToasts();
  const { t } = useTranslation();

  const [showIssueModal] = useIssueModal({
    address: property?.streetAddress ?? "",
    coordinates,
    certificateId: elevationCertificate?.id,
  });

  const [showShareFileModal] = useShareFileModal();

  const [showDeleteDocumentUploadModal, hideDeleteDocumentUploadModal] =
    useModal(() => (
      <Modal onRequestClose={hideDeleteDocumentUploadModal}>
        <DeleteDocumentUploadConfirmation
          isDetailView={isDetailView}
          issuedAt={issuedAt}
          accountDocumentType={accountDocumentType}
          documentUploadId={documentUploadId}
          closeModal={hideDeleteDocumentUploadModal}
          onUpdate={onUpdate}
          address={property?.streetAddress ?? null}
          propertyId={property?.id ?? null}
        />
      </Modal>
    ));

  const { account, authorized, isGuest } = useContext(AuthContext);
  const canShowReportDataIssue = authorized({
    resource: RESOURCE_NAME.ISSUE,
    permission: "create",
  });

  const canViewDocumentDetail = authorized({
    resource: RESOURCE_NAME.DOCUMENT_UPLOAD,
    permission: "view_details",
  });

  const canDeleteDocumentUpload = authorized({
    resource: RESOURCE_NAME.DOCUMENT_UPLOAD,
    permission: "delete",
  });

  const showAdminButton = authorized({
    resource: RESOURCE_NAME.CERTIFICATE,
    permission: "updateSettings",
  });

  const canShareDocumentUpload = authorized({
    resource: RESOURCE_NAME.DOCUMENT_UPLOAD,
    permission: "share",
  });

  const viewLocation = canViewDocumentDetail
    ? {
        href: buildLink(
          isGuest ? "guestDocumentUploadDetail" : "documentUploadDetail",
          {
            id: documentUpload.id,
          },
          {}
        ),
        target: "_blank",
      }
    : {
        href: buildLink(
          isGuest ? "guestDocumentUploadFile" : "documentUploadFile",
          {
            id: documentUpload.id,
          },
          {}
        ),
        target: "_blank",
      };

  const adminSettingsAction: ActionsProps = {
    label: "Admin settings",
    to: {
      pathname: `/backoffice/certificates/${elevationCertificate?.id}`,
    },
    target: "_blank",
  };

  const deleteAction: ActionsProps = {
    label: "Delete",
    onClick: () => showDeleteDocumentUploadModal(),
    disabled: !canDeleteDocumentUpload,
    variant: "red",
  };

  const handleClickExportDocx = async () => {
    setLoadingDocxExport(true);

    track("Clicked 'Download .docx'", {
      documentType: accountDocumentType.name,
      propertyAddress: property?.streetAddress,
    });

    const failureToastText =
      "There was a problem with your download. Please try again or reach out to support@withforerunner.com for help.";

    try {
      const response = await httpClient(
        `/api/files/docx-export/${documentUploadId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        addErrorToast(failureToastText);
        return;
      }

      const blobObject = await response.blob();
      const url = URL.createObjectURL(blobObject);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${originalFilename.split(".")[0]}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      addErrorToast(failureToastText);
    }

    setLoadingDocxExport(false);
  };

  const exportDocxAction: ActionsProps = {
    label: "Download .docx",
    onClick: handleClickExportDocx,
  };

  let downloadAction: ActionsProps = {
    label: t("property-files-dropdown-download"),
    href: buildLink("documentUploadFile", {
      id: documentUpload.id,
    }),
    download: true,
    target: "_blank",
    onClick: () => {
      track("File Downloaded", {
        documentUploadId,
        propertyId: property?.id,
        streetAddress: property?.streetAddress,
        documentTypeName: accountDocumentType.name,
      });
    },
  };

  const reportDataIssueAction: ActionsProps = {
    label: "Report data issue",
    onClick: () => showIssueModal(),
    disabled: !canShowReportDataIssue,
    closeOnClick: false,
  };

  const isInternalPageForPublicEC =
    elevationCertificate && !hiddenFromPublic && !isGuest;
  const shouldShowShareFileAction =
    isInternalPageForPublicEC &&
    account?.publicPortal.enabled &&
    canShareDocumentUpload;

  const shareFileAction: ActionsProps = {
    label: "Share",
    disabled: !canShareDocumentUpload,
    onClick: () => {
      if (shouldShowShareFileAction) {
        track("Clicked 'Share'", { documentUploadId });
        showShareFileModal({
          ...documentUpload,
          elevationCertificate,
        });
      }
    },
  };

  const actions: Array<ActionsProps> = [];
  if (!isDetailView) {
    actions.push({
      label: t("property-files-dropdown-view-file"),
      onClick: createTracker("View File clicked"),
      ...viewLocation,
    });

    if (documentUpload.submission?.id && !isGuest) {
      const viewRecordAction: ActionsProps = {
        label: "View related record",
        href: buildLink(
          "viewSubmission",
          {
            submissionId: documentUpload.submission.id,
          },
          {}
        ),
        target: "_blank",
      };
      actions.push(viewRecordAction);
    }

    actions.push(downloadAction);

    if (isDeletable && !isGuest) {
      actions.push(deleteAction);
    }
  } else {
    if (formData) {
      downloadAction.label = "Download .pdf";
      actions.push(exportDocxAction);
      actions.push(downloadAction);
    } else {
      actions.push(downloadAction);
    }

    if (shouldShowShareFileAction) {
      actions.push(shareFileAction);
    }

    if (documentUpload.submission?.id && !isGuest) {
      const viewRecordAction: ActionsProps = {
        label: "View related record",
        href: buildLink(
          "viewSubmission",
          {
            submissionId: documentUpload.submission.id,
          },
          {}
        ),
        target: "_blank",
      };
      actions.push(viewRecordAction);
    }

    if (isGuest && property) {
      actions.push({
        label: "View property",
        href: buildLink("residentProperty", {
          propertyId: property.id,
        }),
      });
    } else if (!isGuest && shouldShowViewOnMapActionForProperty(property)) {
      actions.push({
        label: "View on map",
        href: buildLink(
          "map",
          {},
          {
            address: property.streetAddress ?? "",
            propertyId: property.id,
            lat: property.latitude.toString(),
            lng: property.longitude.toString(),
          }
        ),
        target: "_blank",
      });
    } else if (
      !isGuest &&
      shouldShowViewOnMapActionForCustomMapGeometry(customMapGeometry)
    ) {
      actions.push({
        label: "View on map",
        href: buildLink(
          "map",
          { accountId: account?.id! },
          {
            geometryId: customMapGeometry.id,
            customMapId: customMapGeometry.customMapId,
          }
        ),
        target: "_blank",
      });
    }

    if (isInternalPageForPublicEC) {
      if (showAdminButton) {
        actions.push(adminSettingsAction);
      }

      actions.push(reportDataIssueAction);
    }

    if (isDeletable && !isGuest) {
      actions.push(deleteAction);
    }
  }

  const actionButton = ({
    onClick,
  }: {
    onClick: (_: React.MouseEvent<HTMLElement>) => void;
  }) => (
    <Button
      onClick={onClick}
      styleVariant="outlineLight"
      rightIconName="chevron-down"
      size="small"
      disabled={loadingDocxExport}
      loading={loadingDocxExport}
      data-testid="actions"
    >
      Actions
    </Button>
  );

  return (
    <Actions
      buttonText={isDetailView ? "Actions" : undefined}
      disabled={disabled}
      actions={actions}
      customButton={isDetailView ? actionButton : undefined}
    />
  );
};

export default ActionButton;
